import { useEffect, createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isRTL, Language, getDirection } from '@/i18n';

// ============================================================================
// TYPES
// ============================================================================

interface RTLContextValue {
  isRTL: boolean;
  direction: 'ltr' | 'rtl';
  language: Language;
  // Logical positioning helpers
  start: 'left' | 'right';
  end: 'left' | 'right';
  // Transform helpers
  flipX: (value: number) => number;
  // Class name helpers
  rtlClass: (ltrClass: string, rtlClass: string) => string;
}

interface RTLWrapperProps {
  children: ReactNode;
  /**
   * Force a specific direction (useful for testing)
   */
  forceDirection?: 'ltr' | 'rtl';
}

// ============================================================================
// CONTEXT
// ============================================================================

const RTLContext = createContext<RTLContextValue>({
  isRTL: false,
  direction: 'ltr',
  language: 'en',
  start: 'left',
  end: 'right',
  flipX: (value) => value,
  rtlClass: (ltrClass) => ltrClass,
});

/**
 * Hook to access RTL context
 */
export const useRTLContext = () => useContext(RTLContext);

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * RTLWrapper component handles RTL (Right-to-Left) layout support
 * Automatically applies direction based on the current language
 */
export const RTLWrapper: React.FC<RTLWrapperProps> = ({ children, forceDirection }) => {
  const { i18n } = useTranslation();
  const currentLanguage = (i18n.language || 'en') as Language;

  // Determine direction
  const isRTLMode = forceDirection ? forceDirection === 'rtl' : isRTL(currentLanguage);
  const direction = forceDirection || getDirection(currentLanguage);

  // Apply RTL styles and attributes to document
  useEffect(() => {
    // Set document direction
    document.documentElement.dir = direction;
    document.documentElement.lang = currentLanguage;

    // Add/remove RTL class for CSS styling
    if (direction === 'rtl') {
      document.body.classList.add('rtl');
      document.body.classList.remove('ltr');
      document.documentElement.setAttribute('data-direction', 'rtl');
    } else {
      document.body.classList.add('ltr');
      document.body.classList.remove('rtl');
      document.documentElement.setAttribute('data-direction', 'ltr');
    }

    // Cleanup
    return () => {
      document.body.classList.remove('rtl', 'ltr');
    };
  }, [direction, currentLanguage]);

  // Memoized context value
  const contextValue = useMemo<RTLContextValue>(() => ({
    isRTL: isRTLMode,
    direction,
    language: currentLanguage,
    start: isRTLMode ? 'right' : 'left',
    end: isRTLMode ? 'left' : 'right',
    flipX: (value: number) => isRTLMode ? -value : value,
    rtlClass: (ltrClass: string, rtlClass: string) => isRTLMode ? rtlClass : ltrClass,
  }), [isRTLMode, direction, currentLanguage]);

  return (
    <RTLContext.Provider value={contextValue}>
      {children}
    </RTLContext.Provider>
  );
};

// ============================================================================
// UTILITY COMPONENTS
// ============================================================================

/**
 * Component that renders different content based on RTL mode
 */
interface RTLSwitchProps {
  ltr: ReactNode;
  rtl: ReactNode;
}

export const RTLSwitch: React.FC<RTLSwitchProps> = ({ ltr, rtl }) => {
  const { isRTL } = useRTLContext();
  return <>{isRTL ? rtl : ltr}</>;
};

/**
 * Component that mirrors its children horizontally in RTL mode
 */
interface RTLMirrorProps {
  children: ReactNode;
  className?: string;
}

export const RTLMirror: React.FC<RTLMirrorProps> = ({ children, className = '' }) => {
  const { isRTL } = useRTLContext();

  return (
    <span
      className={className}
      style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
    >
      {children}
    </span>
  );
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get RTL-aware margin/padding class
 */
export const getLogicalSpacing = (
  property: 'margin' | 'padding',
  side: 'start' | 'end',
  value: string,
  isRTL: boolean
): string => {
  const physicalSide = side === 'start'
    ? (isRTL ? 'r' : 'l')
    : (isRTL ? 'l' : 'r');

  return `${property.charAt(0)}${physicalSide}-${value}`;
};

/**
 * Get RTL-aware text alignment class
 */
export const getTextAlign = (align: 'start' | 'end', isRTL: boolean): string => {
  if (align === 'start') {
    return isRTL ? 'text-right' : 'text-left';
  }
  return isRTL ? 'text-left' : 'text-right';
};

/**
 * Get RTL-aware flex direction
 */
export const getFlexDirection = (
  direction: 'row' | 'row-reverse',
  isRTL: boolean
): 'flex-row' | 'flex-row-reverse' => {
  if (direction === 'row') {
    return isRTL ? 'flex-row-reverse' : 'flex-row';
  }
  return isRTL ? 'flex-row' : 'flex-row-reverse';
};

/**
 * CSS logical properties polyfill object
 */
export const logicalProperties = {
  marginInlineStart: (value: string) => ({
    '[dir="ltr"] &': { marginLeft: value },
    '[dir="rtl"] &': { marginRight: value },
  }),
  marginInlineEnd: (value: string) => ({
    '[dir="ltr"] &': { marginRight: value },
    '[dir="rtl"] &': { marginLeft: value },
  }),
  paddingInlineStart: (value: string) => ({
    '[dir="ltr"] &': { paddingLeft: value },
    '[dir="rtl"] &': { paddingRight: value },
  }),
  paddingInlineEnd: (value: string) => ({
    '[dir="ltr"] &': { paddingRight: value },
    '[dir="rtl"] &': { paddingLeft: value },
  }),
  insetInlineStart: (value: string) => ({
    '[dir="ltr"] &': { left: value },
    '[dir="rtl"] &': { right: value },
  }),
  insetInlineEnd: (value: string) => ({
    '[dir="ltr"] &': { right: value },
    '[dir="rtl"] &': { left: value },
  }),
};

export default RTLWrapper;
